import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  RegistrySearchBox,
  RegistryThSortable,
  RegistryToolbar,
  paginationWindow,
} from '@shared/ui/RegistryShell';
import {
  useCreateGovMutation,
  useListGovQuery,
  useMarkGovSentMutation,
  usePatchGovMutation,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import type { GovRequest } from '@entities/legal/types';
import { useAgentPermission } from '@entities/user';

const PAGE_SIZE = 25;

const AUTHORITY_OPTIONS = [
  { value: '', label: 'Все органы' },
  { value: 'FNS', label: 'ФНС' },
  { value: 'PROSECUTOR', label: 'Прокуратура' },
  { value: 'GOSSTROYNADZOR', label: 'Госстройнадзор' },
  { value: 'ROSTECHNADZOR', label: 'Ростехнадзор' },
  { value: 'CUSTOMS', label: 'Таможня' },
  { value: 'MVD', label: 'МВД' },
  { value: 'FAS', label: 'ФАС' },
  { value: 'ROSPOTREBNADZOR', label: 'Роспотребнадзор' },
  { value: 'OTHER', label: 'Иной орган' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Все стадии' },
  { value: 'new', label: 'Новый' },
  { value: 'in_review', label: 'В разборе' },
  { value: 'drafting', label: 'Готовим ответ' },
  { value: 'sent', label: 'Ответ отправлен' },
  { value: 'closed', label: 'Закрыт' },
  { value: 'contested', label: 'Оспариваем' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Любой приоритет' },
  { value: 'high', label: 'Высокий' },
  { value: 'medium', label: 'Средний' },
  { value: 'low', label: 'Низкий' },
];

const authorityLabel: Record<string, string> = Object.fromEntries(
  AUTHORITY_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);
const statusLabel: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

function priorityTone(p: string): 'danger' | 'warning' | 'success' | 'neutral' {
  if (p === 'high') return 'danger';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'success';
  return 'neutral';
}
function priorityLabel(p: string): string {
  return { high: 'Высокий', medium: 'Средний', low: 'Низкий' }[p] ?? p;
}
function statusTone(s: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'sent':
    case 'closed':
      return 'success';
    case 'contested':
      return 'danger';
    case 'in_review':
    case 'drafting':
      return 'warning';
    default:
      return 'neutral';
  }
}

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

function fmt(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString('ru-RU');
}

function daysLeft(s: string | null): number | null {
  if (!s) return null;
  return Math.round((new Date(s).getTime() - Date.now()) / 86_400_000);
}

export function Gov() {
  const { can_write } = useAgentPermission('statut');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [authority, setAuthority] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [sort, setSort] = useState('deadline');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [search, authority, status, priority, sort, order]);

  const queryArg = useMemo(
    () => ({
      search: search || undefined,
      authority: authority || undefined,
      status: status || undefined,
      priority: priority || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, authority, status, priority, sort, order, page],
  );

  const { data, isLoading, isFetching } = useListGovQuery(queryArg);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [createGov, { isLoading: isCreating }] = useCreateGovMutation();
  const [patchGov] = usePatchGovMutation();
  const [markSent] = useMarkGovSentMutation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<GovRequest | null>(null);
  const [newAuthority, setNewAuthority] = useState('FNS');
  const [requestNo, setRequestNo] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [deadline, setDeadline] = useState('');
  const [isInspection, setIsInspection] = useState(false);

  function toggleSort(field: string) {
    if (sort === field) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setOrder('asc');
    }
  }
  function sortArrow(field: string): string {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  }
  const pages = paginationWindow(page, totalPages);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    await createGov({
      authority: newAuthority,
      request_no: requestNo || undefined,
      subject,
      content_text: content || undefined,
      deadline: deadline ? new Date(`${deadline}T23:59:59Z`).toISOString() : undefined,
      is_inspection: isInspection,
    }).unwrap();
    setRequestNo('');
    setSubject('');
    setContent('');
    setDeadline('');
    setIsInspection(false);
    setOpen(false);
  }

  return (
    <>
      <Note>
        Реестр запросов от государственных органов (ФНС, прокуратура, Госстройнадзор и др.).
        Записи поступают двумя путями: <strong>автоматически</strong> — из писем,
        классифицированных агентом-диспетчером как gov_request; <strong>вручную</strong> —
        кнопкой «+ Новый запрос» для бумажных писем, ГИС-каналов (ЛК ФНС, Госуслуги, СБИС)
        и устных запросов на выездных проверках. Жёсткие сроки, приоритет по типу органа,
        оценка правомерности и подготовка мотивированного ответа.
      </Note>

      <RegistryToolbar>
        <RegistrySearchBox
          placeholder="Поиск по теме или номеру запроса…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <RegistryFilterSelect value={authority} onChange={(e) => setAuthority(e.target.value)}>
          {AUTHORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect value={priority} onChange={(e) => setPriority(e.target.value)}>
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <Button
          onClick={() => setOpen(true)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          + Новый запрос
        </Button>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
      </RegistryBar>

      <Card>
        <Table>
          <thead>
            <tr>
              <RegistryThSortable
                $active={sort === 'authority'}
                onClick={() => toggleSort('authority')}
              >
                Орган{sortArrow('authority')}
              </RegistryThSortable>
              <th>Тема</th>
              <RegistryThSortable
                $active={sort === 'received_at'}
                onClick={() => toggleSort('received_at')}
              >
                Поступил{sortArrow('received_at')}
              </RegistryThSortable>
              <RegistryThSortable
                $active={sort === 'deadline'}
                onClick={() => toggleSort('deadline')}
              >
                Срок{sortArrow('deadline')}
              </RegistryThSortable>
              <RegistryThSortable
                $active={sort === 'priority'}
                onClick={() => toggleSort('priority')}
              >
                Приоритет{sortArrow('priority')}
              </RegistryThSortable>
              <RegistryThSortable $active={sort === 'status'} onClick={() => toggleSort('status')}>
                Стадия{sortArrow('status')}
              </RegistryThSortable>
              <th>Источник</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width={120} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="70%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={100} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={100} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={80} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={100} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={70} $height={20} $radius="999px" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={7}>
                  {search || authority || status || priority
                    ? 'По заданным фильтрам ничего не найдено.'
                    : 'Запросов от госорганов пока не поступало.'}
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((r) => {
                const days = daysLeft(r.deadline);
                const urgent = days != null && days <= 3;
                const overdue = days != null && days < 0;
                return (
                  <tr key={r.id} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                    <td>
                      <Badge>{authorityLabel[r.authority] ?? r.authority}</Badge>
                      {r.is_inspection && (
                        <Badge $tone="warning" style={{ marginLeft: 6 }}>
                          проверка
                        </Badge>
                      )}
                    </td>
                    <td>
                      <div>{r.subject}</div>
                      {r.request_no && <Mono>№ {r.request_no}</Mono>}
                    </td>
                    <td>{fmt(r.received_at)}</td>
                    <td
                      style={{
                        color: overdue
                          ? 'var(--osnova-danger, #d04848)'
                          : urgent
                            ? 'var(--osnova-warning, #d99c2a)'
                            : undefined,
                        fontWeight: urgent || overdue ? 600 : undefined,
                      }}
                    >
                      {r.deadline ? fmt(r.deadline) : '—'}
                      {days != null && (
                        <div style={{ fontSize: 11 }}>
                          {overdue
                            ? `просрочено на ${-days} дн.`
                            : days === 0
                              ? 'сегодня'
                              : `${days} дн.`}
                        </div>
                      )}
                    </td>
                    <td>
                      <Badge $tone={priorityTone(r.priority)}>{priorityLabel(r.priority)}</Badge>
                    </td>
                    <td>
                      <Badge $tone={statusTone(r.status)}>
                        {statusLabel[r.status] ?? r.status}
                      </Badge>
                    </td>
                    <td>
                      {r.source === 'auto' ? (
                        <Badge $tone="info" title="Создано автоматически из письма">
                          из письма
                        </Badge>
                      ) : (
                        <Badge title="Введено юристом вручную">вручную</Badge>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Card>

      {total > PAGE_SIZE && (
        <RegistryPagination>
          <span>
            Стр. {page + 1} из {totalPages} · показано {items.length} из {total}
          </span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Назад
            </RegistryPageBtn>
            {pages.map((p, idx) => {
              const prev = idx > 0 ? pages[idx - 1] : -1;
              const gap = idx > 0 && p - prev > 1;
              return (
                <Fragment key={p}>
                  {gap && <RegistryPageBtn disabled>…</RegistryPageBtn>}
                  <RegistryPageBtn type="button" $active={p === page} onClick={() => setPage(p)}>
                    {p + 1}
                  </RegistryPageBtn>
                </Fragment>
              );
            })}
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Вперёд →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      <Modal open={open} title="Новый запрос от госоргана" onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <Field>
            <Label htmlFor="gov-auth">Орган</Label>
            <Select
              id="gov-auth"
              value={newAuthority}
              onChange={(e) => setNewAuthority(e.target.value)}
            >
              {AUTHORITY_OPTIONS.filter((o) => o.value).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label htmlFor="gov-no">Номер запроса (если есть)</Label>
            <Input id="gov-no" value={requestNo} onChange={(e) => setRequestNo(e.target.value)} />
          </Field>
          <Field>
            <Label htmlFor="gov-subj">Тема *</Label>
            <Input
              id="gov-subj"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="gov-content">Содержание запроса</Label>
            <textarea
              id="gov-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              style={{
                width: '100%',
                minHeight: 100,
                padding: 8,
                borderRadius: 6,
                border: '1px solid var(--osnova-border, #2a2a2a)',
                background: 'var(--osnova-bg-elevated, #1a1a1a)',
                color: 'inherit',
                font: 'inherit',
                fontSize: 13,
                resize: 'vertical',
              }}
            />
          </Field>
          <Field>
            <Label htmlFor="gov-deadline">
              Срок ответа (если не указан — рассчитается автоматически)
            </Label>
            <Input
              id="gov-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </Field>
          <Field>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={isInspection}
                onChange={(e) => setIsInspection(e.target.checked)}
              />
              Это запрос в рамках проверки
            </label>
          </Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              type="submit"
              disabled={isCreating || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isCreating ? 'Сохраняем…' : 'Создать'}
            </Button>
            <Button type="button" $variant="secondary" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal>

      {selected && (
        <GovCard
          gov={selected}
          onClose={() => setSelected(null)}
          onPatch={async (body) => {
            await patchGov({ id: selected.id, ...body }).unwrap();
          }}
          onSend={async () => {
            await markSent(selected.id).unwrap();
          }}
        />
      )}
    </>
  );
}

interface CardProps {
  gov: GovRequest;
  onClose: () => void;
  onPatch: (body: {
    status?: string;
    response_text?: string;
    legality_check?: string;
    notes?: string;
  }) => Promise<void>;
  onSend: () => Promise<void>;
}

function GovCard({ gov, onClose, onPatch, onSend }: CardProps) {
  const { can_write } = useAgentPermission('statut');
  const [response, setResponse] = useState(gov.response_text ?? '');
  const [legality, setLegality] = useState(gov.legality_check ?? '');
  const [notes, setNotes] = useState(gov.notes ?? '');
  const [busy, setBusy] = useState(false);

  async function save(extra: Partial<{ status: string }> = {}) {
    setBusy(true);
    try {
      await onPatch({
        response_text: response || undefined,
        legality_check: legality || undefined,
        notes: notes || undefined,
        ...extra,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open title={gov.subject} onClose={onClose}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr',
          gap: 12,
          fontSize: 13,
        }}
      >
        <span style={{ color: 'var(--osnova-text-muted, #888)' }}>Орган</span>
        <strong>{authorityLabel[gov.authority] ?? gov.authority}</strong>
        {gov.request_no && (
          <>
            <span style={{ color: 'var(--osnova-text-muted, #888)' }}>№ запроса</span>
            <Mono>{gov.request_no}</Mono>
          </>
        )}
        <span style={{ color: 'var(--osnova-text-muted, #888)' }}>Поступил</span>
        <span>{fmt(gov.received_at)}</span>
        <span style={{ color: 'var(--osnova-text-muted, #888)' }}>Срок ответа</span>
        <span>{fmt(gov.deadline)}</span>
        <span style={{ color: 'var(--osnova-text-muted, #888)' }}>Приоритет</span>
        <Badge $tone={priorityTone(gov.priority)}>{priorityLabel(gov.priority)}</Badge>
        <span style={{ color: 'var(--osnova-text-muted, #888)' }}>Стадия</span>
        <Badge $tone={statusTone(gov.status)}>{statusLabel[gov.status] ?? gov.status}</Badge>
      </div>

      {gov.content_text && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: 'var(--osnova-surface, rgba(255,255,255,0.04))',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            maxHeight: 220,
            overflow: 'auto',
          }}
        >
          {gov.content_text}
        </div>
      )}

      <Field style={{ marginTop: 16 }}>
        <Label htmlFor="gov-legality">Оценка правомерности</Label>
        <Select id="gov-legality" value={legality} onChange={(e) => setLegality(e.target.value)}>
          <option value="">— не оценена —</option>
          <option value="lawful">Запрос правомерен</option>
          <option value="partially_lawful">Частично правомерен</option>
          <option value="unlawful">Неправомерен (оспариваем)</option>
        </Select>
      </Field>
      <Field>
        <Label htmlFor="gov-response">Черновик ответа</Label>
        <textarea
          id="gov-response"
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          style={{
            width: '100%',
            minHeight: 140,
            padding: 8,
            borderRadius: 6,
            border: '1px solid var(--osnova-border, #2a2a2a)',
            background: 'var(--osnova-bg-elevated, #1a1a1a)',
            color: 'inherit',
            font: 'inherit',
            fontSize: 13,
            resize: 'vertical',
          }}
        />
      </Field>
      <Field>
        <Label htmlFor="gov-notes">Внутренние заметки</Label>
        <textarea
          id="gov-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{
            width: '100%',
            minHeight: 60,
            padding: 8,
            borderRadius: 6,
            border: '1px solid var(--osnova-border, #2a2a2a)',
            background: 'var(--osnova-bg-elevated, #1a1a1a)',
            color: 'inherit',
            font: 'inherit',
            fontSize: 13,
            resize: 'vertical',
          }}
        />
      </Field>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        <Button
          type="button"
          onClick={() => save()}
          disabled={busy || !can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        {gov.status !== 'sent' && gov.status !== 'closed' && (
          <Button
            type="button"
            $variant="secondary"
            onClick={async () => {
              await save();
              await onSend();
              onClose();
            }}
            disabled={busy || !response.trim() || !can_write}
            title={
              !can_write
                ? 'Только чтение'
                : !response.trim()
                  ? 'Сначала впишите ответ'
                  : 'Отметить ответ отправленным'
            }
          >
            Отметить отправленным
          </Button>
        )}
        <Button
          type="button"
          $variant="secondary"
          onClick={() => save({ status: 'contested' })}
          disabled={busy || !can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          Оспаривать
        </Button>
        <Button
          type="button"
          $variant="secondary"
          onClick={() => save({ status: 'closed' })}
          disabled={busy || !can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          Закрыть
        </Button>
      </div>
    </Modal>
  );
}
